import { formatThaiShortDateTwoDigitYear } from 'common/utils/date'
import { fNumber } from 'common/utils/formatNumber'
import { useGetTableComparison } from 'modules/comparisonPond/api/queries'
import { TableComparisonResponse } from 'modules/comparisonPond/api/types'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router-dom'

import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  Avatar,
  Typography,
} from '@mui/material'

import NoData from 'components/NoData'

import ComparisonTableSkeleton from './Skeleton'

const ComparisonTable: React.FC = () => {
  const { t } = useTranslation('pondComparison')
  const [searchParams] = useSearchParams()
  const idParam = searchParams.get('cycleIds')
  const urlCycleIds = idParam ? idParam.split(',').filter(Boolean) : []
  const parsedCycleIds = urlCycleIds.map((id) => id.toString())
  const { data, isLoading } = useGetTableComparison(parsedCycleIds)
  const hasSelection = Array.isArray(parsedCycleIds) && parsedCycleIds.length > 0
  const isFish = data?.[0]?.species.id === 2
  const speciesName = data?.[0]?.species?.name || ''

  const tableRows = [
    {
      label: t('age', { speciesName }),
      key: 'age',
      unit: t('unit.day'),
      icon: isFish
        ? '/static/icons/pondComparison/fish.svg'
        : '/static/icons/pondComparison/shrimp.svg',
    },
    {
      label: t('fcr'),
      key: 'fcr',
      unit: '',
      icon: '/static/icons/pondComparison/fcr.svg',
    },
    {
      label: t('adg'),
      key: 'adg',
      unit: '',
      icon: '/static/icons/pondComparison/adg.svg',
    },
    {
      label: t('survival_rate'),
      key: 'survival_rate',
      unit: '%',
      icon: '/static/icons/pondComparison/survivalRate.svg',
    },
    {
      label: t('initial_shrimp_count', { speciesName }),
      key: 'initial_shrimp_count',
      unit: t('unit.count'),
      icon: '/static/icons/pondComparison/feedAmount.svg',
    },
    {
      label: t('shrimp_size', { speciesName }),
      key: 'shrimp_count_per_kg',
      unit: t('unit.one_per_gram'),
      icon: '/static/icons/pondComparison/shrimp_count_per_kg.svg',
    },
    {
      // ผลผลิต(ตัว)
      label: t('shrimp_amount'),
      key: 'total_yield_count',
      unit: t('unit.count'),
      icon: '/static/icons/pondComparison/shrimp_amount.svg',
    },
    {
      label: t('accumulated_food'),
      key: 'accumulated_food',
      unit: t('unit.kg'),
      icon: '/static/icons/pondComparison/accumulated_food.svg',
    },
    {
      // จำนวนสัตว์ตายสะสม(ตัว)
      label: t('accumulated_dead_shrimp', { speciesName }),
      key: 'accumulated_dead_shrimp_count',
      unit: t('unit.count'),
      icon: '/static/icons/pondComparison/accumulated_dead_shrimp.svg',
    },
    {
      label: t('cost'),
      key: 'cost',
      unit: t('unit.baht'),
      icon: '/static/icons/pondComparison/cost.svg',
    },
    {
      label: t('revenue'),
      key: 'revenue',
      unit: t('unit.baht'),
      icon: '/static/icons/pondComparison/revenue.svg',
    },
    {
      label: t('profit'),
      key: 'profit',
      unit: t('unit.baht'),
      icon: '/static/icons/pondComparison/profit.svg',
    },
  ]

  // DOF-1357: rows that depend on the estimated yield
  const YIELD_DEPENDENT_KEYS = [
    'total_yield_weight_kg',
    'total_yield_count',
    'shrimp_amount',
    'fcr',
    'survival_rate',
  ]

  const renderCellValue = (val: any, unit?: string) => {
    if (val === null || val === undefined || val === '') return '-'
    const num = Number(val)
    if (!Number.isNaN(num)) {
      return (
        <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'flex-end', gap: 0.5 }}>
          <Typography>{fNumber(num)}</Typography>
          <Typography sx={{ fontWeight: 400 }}>{unit}</Typography>
        </Box>
      )
    }
    return (
      <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'flex-end', gap: 0.5 }}>
        <Typography>{String(val)}</Typography>
        <Typography sx={{ fontWeight: 400 }}>{unit}</Typography>
      </Box>
    )
  }

  if (isLoading && hasSelection) {
    return <ComparisonTableSkeleton />
  }

  if (!data || data.length === 0) {
    return <NoData />
  }

  return (
    <TableContainer>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell
              sx={{
                fontWeight: 600,
                minWidth: 150,
                color: 'text.primary',
                fontSize: 'body1.fontSize',
              }}
            >
              {t('items')}
            </TableCell>
            {data.map((pond: TableComparisonResponse) => (
              <TableCell
                key={pond.cycle_id}
                align="left"
                sx={{ fontWeight: 600, minWidth: 120, pl: 0, pr: 0 }}
              >
                <Box
                  sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1 }}
                >
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 600, color: 'text.primary', fontSize: 'body1.fontSize' }}
                  >
                    {t('optionLabel', {
                      farm: pond.farm_name,
                      pond: pond.pond_name,
                      start: formatThaiShortDateTwoDigitYear(pond.start_date),
                      end: pond.end_date ? formatThaiShortDateTwoDigitYear(pond.end_date) : '',
                    })}
                  </Typography>
                  <Typography></Typography>
                </Box>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {tableRows.map((row) => (
            <TableRow key={row.key} hover>
              <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Avatar
                    src={row.icon}
                    alt={row.label}
                    variant="rounded"
                    sx={{ width: 28, height: 28 }}
                  />
                  <Box sx={{ display: 'flex', flexDirection: 'column', lineHeight: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {row.label}
                    </Typography>
                    {row.unit && (
                      <Typography variant="caption" color="text.secondary">
                        ({row.unit})
                      </Typography>
                    )}
                  </Box>
                </Box>
              </TableCell>
              {data.map((pond: TableComparisonResponse) => (
                <TableCell sx={{ p: 0 }} key={`${pond.cycle_id}-${row.key}`} align="left">
                  {renderCellValue(
                    pond.yield_estimatable === false && YIELD_DEPENDENT_KEYS.includes(row.key)
                      ? null
                      : (pond as Record<string, any>)[row.key],
                    row.unit
                  )}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  )
}

export default ComparisonTable
